import type {
  SegmentAggregatedCardsResponse,
  SegmentAggregatedDataAPI,
} from '@trinity/chat/types/segment-aggregated-data';
import type { SegmentComparisonAPI } from '@trinity/chat/types/segment-comparison-aggregated-data';
import type { SegmentationInputs } from '@trinity/chat/types/segmentation-inputs';
import { getAuthenticatedClient } from '@/api/client';
import { QualMode, QualPersonasData, SegmentationStage } from '../state/sessionStore';

const BASE = '/segmentation';

function client() {
  return getAuthenticatedClient();
}

export function createSession(): Promise<{ session_id: string }> {
  return client()
    .post(`${BASE}/session`)
    .then((r) => r.data);
}

export function uploadFile(
  file: File,
  sessionId?: string,
  forceReprocess = true
): Promise<{ session_id: string }> {
  const fd = new FormData();
  fd.append('file', file);
  if (sessionId) fd.append('session_id', sessionId);
  if (forceReprocess) fd.append('force_reprocess', 'true');
  return client()
    .post(`${BASE}/upload`, fd)
    .then((r) => r.data);
}

export function getStatus(sessionId: string): Promise<StatusResponse> {
  return client()
    .get(`${BASE}/status/${sessionId}`)
    .then((r) => r.data);
}

export function getSessionState(sessionId: string): Promise<SessionState> {
  return client()
    .get(`${BASE}/session/${sessionId}/state`)
    .then((r) => r.data);
}

export function getEDA(sessionId: string): Promise<EDAResponse> {
  return client()
    .get(`${BASE}/eda/${sessionId}`)
    .then((r) => r.data);
}

export function regenerateEDA(sessionId: string): Promise<EDAResponse> {
  return client()
    .post(`${BASE}/eda/${sessionId}/regenerate`)
    .then((r) => r.data);
}

export function getFeatures(sessionId: string): Promise<FeaturesResponse> {
  return client()
    .get(`${BASE}/features/${sessionId}`)
    .then((r) => r.data);
}

export function getFeatureData(
  sessionId: string,
  limit = 5000,
  source: 'curated' | 'raw' = 'curated'
): Promise<FeatureDataResponse> {
  return client()
    .get(`${BASE}/features/${sessionId}/data`, { params: { limit, source } })
    .then((r) => r.data);
}

export function getFeatureExclusionLog(sessionId: string): Promise<ExclusionLogEntry[]> {
  return client()
    .get(`${BASE}/features/${sessionId}`)
    .then((r) => r.data.exclusion_log || []);
}

export function overrideFeature(sessionId: string, body: FeatureOverrideRequest): Promise<unknown> {
  return client()
    .put(`${BASE}/features/${sessionId}/override`, body)
    .then((r) => r.data);
}

export function setObjective(sessionId: string, body: ObjectiveRequest): Promise<ObjectiveResponse> {
  return client()
    .post(`${BASE}/objective/${sessionId}`, body)
    .then((r) => r.data);
}

export function getObjective(sessionId: string): Promise<ObjectiveResponse> {
  return client()
    .get(`${BASE}/objective/${sessionId}`)
    .then((r) => r.data);
}

export function listStrategies(): Promise<Strategy[]> {
  return client()
    .get(`${BASE}/strategies`)
    .then((r) => r.data);
}

export function runSegmentation(sessionId: string, body: Record<string, unknown> = {}): Promise<unknown> {
  return client()
    .post(`${BASE}/segment/${sessionId}`, body)
    .then((r) => r.data);
}

export function getSegments(sessionId: string): Promise<unknown> {
  return client()
    .get(`${BASE}/segment/${sessionId}`)
    .then((r) => r.data);
}

export function getProfiles(sessionId: string): Promise<ProfilesResponse> {
  return client()
    .get(`${BASE}/profiles/${sessionId}`)
    .then((r) => r.data);
}

export function sendChat(sessionId: string, message: string): Promise<ChatResponse> {
  return client()
    .post(`${BASE}/chat/${sessionId}`, { message })
    .then((r) => r.data);
}

export function sendClaimsChatQuery(
  query: string,
  claimsSessionId?: string | null
): Promise<{ requestId: string }> {
  return client()
    .post(`${BASE}/claims-chat/query`, {
      query,
      session_id: claimsSessionId,
      include_visualization: false,
    })
    .then((r) => r.data);
}

export function getConfig(): Promise<SegmentationConfig> {
  return client()
    .get(`${BASE}/config`)
    .then((r) => r.data);
}

export function saveConfig(data: Partial<SegmentationConfig>): Promise<SegmentationConfig> {
  return client()
    .post(`${BASE}/config`, data)
    .then((r) => r.data);
}

export function resetConfig(): Promise<SegmentationConfig> {
  return client()
    .post(`${BASE}/config/reset`)
    .then((r) => r.data);
}

export function getDefaults(): Promise<SegmentationConfig> {
  return client()
    .get(`${BASE}/defaults`)
    .then((r) => r.data);
}

export function uploadQual(sessionId: string, file: File): Promise<unknown> {
  const fd = new FormData();
  fd.append('file', file);
  return client()
    .post(`${BASE}/qual-upload/${sessionId}`, fd)
    .then((r) => r.data);
}

export function sendQualText(sessionId: string, text: string, sourceLabel = 'pasted_text'): Promise<unknown> {
  return client()
    .post(`${BASE}/qual-text/${sessionId}`, { text, source_label: sourceLabel })
    .then((r) => r.data);
}

export function getQualThemes(sessionId: string): Promise<QualThemesResponse> {
  return client()
    .get(`${BASE}/qual/${sessionId}/themes`)
    .then((r) => r.data);
}

export function updateQualThemes(sessionId: string, themes: QualTheme[]): Promise<unknown> {
  return client()
    .put(`${BASE}/qual/${sessionId}/themes`, { themes })
    .then((r) => r.data);
}

export function listServerFiles(): Promise<ServerFile[]> {
  return client()
    .get(`${BASE}/files`)
    .then((r) => r.data);
}

export function selectServerFile(
  filename: string,
  sessionId?: string,
  forceReprocess = true
): Promise<{ session_id: string }> {
  const fd = new FormData();
  fd.append('filename', filename);
  if (sessionId) fd.append('session_id', sessionId);
  if (forceReprocess) fd.append('force_reprocess', 'true');
  return client()
    .post(`${BASE}/select-file`, fd)
    .then((r) => r.data);
}

// --- Brand Discovery & Configuration ---

export function createSessionFromCache(
  fileHash: string,
  uploadFilename = ''
): Promise<{
  session_id: string;
  status: string;
  stage: string;
  pipeline_source: string;
  meta: Record<string, unknown>;
}> {
  const fd = new FormData();
  fd.append('file_hash', fileHash);
  fd.append('upload_filename', uploadFilename);
  return client()
    .post(`${BASE}/session/from-cache`, fd)
    .then((r) => r.data);
}

export function getBrands(sessionId: string): Promise<{ brands: BrandInfo[] }> {
  return client()
    .get(`${BASE}/brands/${sessionId}`)
    .then((r) => r.data);
}

export function previewVolumeFilter(
  sessionId: string,
  volumeFilter: number
): Promise<Record<string, unknown>> {
  return client()
    .get(`${BASE}/brands/${sessionId}/preview`, { params: { volume_filter: volumeFilter } })
    .then((r) => r.data);
}

export function configureSession(
  sessionId: string,
  body: ConfigureRequest
): Promise<Record<string, unknown>> {
  return client()
    .post(`${BASE}/configure/${sessionId}`, body)
    .then((r) => r.data);
}

export function getBrandEDA(sessionId: string): Promise<Record<string, unknown>> {
  return client()
    .get(`${BASE}/brand-eda/${sessionId}`)
    .then((r) => r.data);
}

export function getSegmentMarketViews(sessionId: string): Promise<SegmentMarketViewsResponse> {
  return client()
    .get(`${BASE}/segment-market-views/${sessionId}`)
    .then((r) => r.data);
}

export interface SegmentMarketViewItem {
  id: number;
  name: string;
  n_hcps: number;
  n_patients: number;
  market_kpis: Record<string, unknown>;
  hcp_characteristics: Record<string, unknown>;
  treatment_trends: Record<string, unknown>;
  // Additional fields from playground qual_overlay_updates branch
  [key: string]: unknown; // Allow any additional fields from API
}

export interface SegmentMarketViewsResponse {
  segments: SegmentMarketViewItem[];
  segment_insights?: Record<string, SegmentInsight>;
}

// ─── Segment Insights (Qual-Quant Overlay) ──────────────────────────────────

export interface Quote {
  text: string;
  source: string;
}

export interface PersonaMatch {
  persona_id: string;
  label: string;
  score: number;
  quality: 'strong' | 'moderate' | 'weak';
}

export interface ConfirmingSignal {
  signal: string;
  claims_evidence: string;
  qual_evidence: string;
  obs_brand_id?: string;
  obs_evidence_basis?: string;
  obs_content_topic?: string;
  obs_intensity?: 'high' | 'medium' | 'low';
  quote?: Quote;
  research_note?: string;
}

export interface EnrichingInsight {
  insight: string;
  obs_brand_id?: string;
  obs_evidence_basis?: string;
  quote?: Quote;
  brand_specific: boolean;
  brand_note?: string;
}

export interface Tension {
  type: 'fragile_loyalty' | 'latent_opportunity' | 'unsustainable_growth' | 'lagging_data';
  label: string;
  severity: 'high' | 'medium' | 'low';
  claims_signal: string;
  interview_signal: string;
  recommendation: string;
  research_note?: string;
}

export interface ObservationItem {
  summary: string;
  brand_id?: string;
  evidence_basis?: string;
  intensity?: 'high' | 'medium' | 'low';
  quote?: string;
}

export interface BrandInsight {
  brand_id: string;
  is_focal: boolean;
  positive: number;
  negative: number;
  neutral: number;
  mixed: number;
  top_topics: Record<string, number>;
  top_barriers: string[];
  top_drivers: string[];
}

export interface MessagingQuote {
  quote: string;
  brand_id?: string;
  content_topic?: string;
  evidence_basis?: string;
}

export interface ObservationEnrichment {
  n_observations: number;
  n_respondents: number;
  barriers: ObservationItem[];
  drivers: ObservationItem[];
  brand_insights: BrandInsight[];
  messaging_quotes: MessagingQuote[];
}

export interface SourceConvergence {
  claims_qual_agree: boolean;
  research_agrees: boolean;
  triple_convergence: boolean;
}

export interface SegmentInsight {
  segment_id: string;
  persona_match?: PersonaMatch;
  is_persona_based: boolean;
  mode_framing: string;
  confirming: ConfirmingSignal[];
  enriching: EnrichingInsight[];
  tensions: Tension[];
  no_coverage: boolean;
  research_recommendation?: string;
  source_convergence: SourceConvergence;
  observation_enrichment: ObservationEnrichment;
}

export function rerunFeatures(
  sessionId: string,
  categoryWeights: Record<string, number>,
  userReasoning = ''
): Promise<ObjectiveResponse> {
  return client()
    .post(`${BASE}/features/${sessionId}/rerun`, {
      category_weights: categoryWeights,
      user_reasoning: userReasoning,
    })
    .then((r) => r.data);
}

export function resetWeights(
  sessionId: string
): Promise<{ status: string; category_weights: Record<string, number>; strategy: string }> {
  return client()
    .post(`${BASE}/features/${sessionId}/reset-weights`)
    .then((r) => r.data);
}

export async function downloadSegmentedExcel(sessionId: string): Promise<void> {
  let resp;
  try {
    resp = await client().get(`${BASE}/export/${sessionId}`, { responseType: 'blob', timeout: 300_000 });
  } catch (err) {
    // axios with responseType: 'blob' wraps error bodies as Blob — read them as text so the
    // real upstream message surfaces instead of a generic 500.
    const e = err as { response?: { data?: unknown; status?: number }; message?: string };
    const data = e.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const json = JSON.parse(text);
        throw new Error(json.error || json.message || text || `HTTP ${e.response?.status}`);
      } catch {
        throw new Error(text || `HTTP ${e.response?.status}`);
      }
    }
    throw new Error(e.message || 'Export request failed');
  }
  // If server responded 2xx but with a JSON error blob (proxy quirk), surface that too.
  const ct = (resp.headers as Record<string, string>)['content-type'] || '';
  if (ct.includes('application/json')) {
    const text = await (resp.data as Blob).text();
    try {
      const json = JSON.parse(text);
      throw new Error(json.error || json.message || text);
    } catch {
      throw new Error(text);
    }
  }
  const cd = (resp.headers as Record<string, string>)['content-disposition'] || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : 'segmented_data.xlsx';
  const blob = new Blob([resp.data as BlobPart], { type: ct });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export async function downloadSegmentedPowerpoint(sessionId: string): Promise<void> {
  let resp;
  try {
    resp = await client().post(`${BASE}/export-ppt/${sessionId}`, undefined, {
      responseType: 'blob',
      timeout: 300_000,
    });
  } catch (err) {
    const e = err as { response?: { data?: unknown; status?: number }; message?: string };
    const data = e.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const json = JSON.parse(text);
        throw new Error(json.error || json.message || text || `HTTP ${e.response?.status}`);
      } catch {
        throw new Error(text || `HTTP ${e.response?.status}`);
      }
    }
    throw new Error(e.message || 'PPT export request failed');
  }
  const ct = (resp.headers as Record<string, string>)['content-type'] || '';
  if (ct.includes('application/json')) {
    const text = await (resp.data as Blob).text();
    try {
      const json = JSON.parse(text);
      throw new Error(json.error || json.message || text);
    } catch {
      throw new Error(text);
    }
  }
  const cd = (resp.headers as Record<string, string>)['content-disposition'] || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : 'segmentation_deck.pptx';
  const blob = new Blob([resp.data as BlobPart], { type: ct });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export async function downloadSegmentedAllZip(sessionId: string): Promise<void> {
  let resp;
  try {
    resp = await client().post(`${BASE}/export-all/${sessionId}`, undefined, {
      responseType: 'blob',
      timeout: 300_000,
    });
  } catch (err) {
    const e = err as { response?: { data?: unknown; status?: number }; message?: string };
    const data = e.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const json = JSON.parse(text);
        throw new Error(json.error || json.message || text || `HTTP ${e.response?.status}`);
      } catch {
        throw new Error(text || `HTTP ${e.response?.status}`);
      }
    }
    throw new Error(e.message || 'Bundle export request failed');
  }
  const ct = (resp.headers as Record<string, string>)['content-type'] || '';
  if (ct.includes('application/json')) {
    const text = await (resp.data as Blob).text();
    try {
      const json = JSON.parse(text);
      throw new Error(json.error || json.message || text);
    } catch {
      throw new Error(text);
    }
  }
  const cd = (resp.headers as Record<string, string>)['content-disposition'] || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `segmentation_${sessionId}.zip`;
  const blob = new Blob([resp.data as BlobPart], { type: ct });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// --- Page listing & creation (for persisted segment dashboards) ---

export function getSegmentPages(): Promise<{
  pages: { id: number; name: string; createdOn: string; providerData: Record<string, unknown> }[];
}> {
  return client()
    .get('/pages', { params: { type: 'custom_dashboard' } })
    .then((r) => r.data);
}

export function createSegmentPage(
  name: string,
  segmentData?: Record<string, unknown>
): Promise<{ id: number }> {
  return client()
    .post('/pages', {
      name,
      type: 'custom_dashboard',
      providerData: segmentData ? { segmentProfile: segmentData } : undefined,
    })
    .then((r) => r.data);
}

export function savePageVersion(pageId: number, config: Record<string, unknown>): Promise<unknown> {
  return client()
    .post(`/pages/${pageId}/versions`, { config, description: 'Auto-generated from segmentation' })
    .then((r) => r.data);
}

// --- Types ---

export interface StatusLogEntry {
  timestamp?: string;
  stage?: string;
  step?: string;
  message: string;
  progress_pct?: number | null;
}

export interface StatusResponse {
  log: StatusLogEntry[];
  message: string;
  progress_pct: number;
  current_stage?: string;
  current_step?: string;
}

export interface BrandInfo {
  molecule: string;
  product_form: string;
  brand_id: string;
  total_claims: number;
  total_patients: number;
  total_hcps: number;
  market_share_pct: number;
}

export interface ConfigureRequest {
  focal_brand_ids?: string[];
  volume_filter?: number;
  period_split?: string;
  regimen_coverage_pct?: number;
  large_center_hcp_threshold?: number;
}

export interface SessionState {
  qual_mode: QualMode;
  stage: SegmentationStage;
  segment_error?: string;
  profiling_error?: string;
  [key: string]: unknown;
}

export interface EDAResponse {
  eda_report: EDAReport;
  charts: Record<string, unknown>;
  narrative: string;
}

export interface EDAReport {
  dataset_summary: DatasetSummary;
  hcp_level: HCPLevel;
  [key: string]: unknown;
}

export interface DatasetSummary {
  total_hcps: number;
  total_rows: number;
  time_periods: number;
  [key: string]: unknown;
}

export interface HCPLevel {
  count: number;
  segmentable_hcps?: {
    hcps_included?: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface FeaturesResponse {
  features: Record<string, unknown>;
  exclusion_log: ExclusionLogEntry[];
}

export interface FeatureDataResponse {
  rows: Record<string, unknown>[];
  columns: string[];
  total_hcps: number;
}

export interface ExclusionLogEntry {
  feature: string;
  reason: string;
  [key: string]: unknown;
}

export interface FeatureOverrideRequest {
  feature_name: string;
  action: 'include' | 'exclude';
  [key: string]: unknown;
}

export interface ObjectiveRequest {
  strategy_key?: string;
  nl_input?: string;
  [key: string]: unknown;
}

export interface ObjectiveResponse {
  final_feature_set: FeatureSelection;
  category_weights: Record<string, number>;
  _navigateOnly?: boolean; // internal flag to indicate no state update needed, just navigate to next phase
  [key: string]: unknown;
}

export interface FeatureSelection {
  categories: Record<string, FeatureCategory>;
  [key: string]: unknown;
}

export interface FeatureCategory {
  weight: number;
  features: string[];
  [key: string]: unknown;
}

export interface Strategy {
  key: string;
  label: string;
  description: string;
}

export interface ProfilesResponse {
  segments: SegmentProfile[];
  validation: ValidationDetails;
  [key: string]: unknown;
}

export interface SegmentProfile {
  id: number;
  name: string;
  narrative: string;
  size: number;
  pct: number;
  commercial_actions: string[];
  gap_analysis: string;
  stats: Record<string, number>;
  /** Management-API page id for the rich segment profile view. Null when the
   *  run hasn't been saved to pages yet. */
  dashboardPageId?: number | null;
  [key: string]: unknown;
}

export interface ValidationDetails {
  silhouette_score: number;
  stability: number;
  [key: string]: unknown;
}

export interface ChatResponse {
  type: 'action' | 'context' | 'question' | 'text' | 'error';
  message: string;
  [key: string]: unknown;
}

export interface SegmentationConfig {
  [key: string]: unknown;
}

export interface QualThemesResponse {
  themes: QualTheme[];
}

export interface QualTheme {
  id: string;
  name: string;
  description: string;
  [key: string]: unknown;
}

export interface ServerFile {
  filename: string;
  size: number;
  [key: string]: unknown;
}

// ─── Segmentation Runs (v2 normalized schema) ───────────────────────────────

export interface SegmentationRunFile {
  kind: 'quant' | 'qual';
  path: string;
  filename: string;
  extension?: string | null;
  sizeBytes?: number | null;
  workspaceFileId?: number | null;
}

export interface CreateSegmentationRunBody {
  projectId: number;
  runKey: string;
  sessionId?: string | null;
  status?: 'incomplete' | 'complete';
  name?: string | null;
  dataSourceName?: string | null;
  projectTitle?: string | null;
  projectBrand?: string | null;
  segmentCount?: number | null;
  totalPatientPopulation?: number | null;
  runTimestamp?: string | null;
  marketView?: Record<string, unknown> | null;
  qualInsights?: Record<string, unknown> | null;
  profilesRoot?: Record<string, unknown> | null;
  qualPostSeg?: Record<string, unknown> | null;
  segmentationInputs?: SegmentationInputs | null;
  files?: SegmentationRunFile[];
}

export interface SegmentationRunSummary {
  id: number;
  identifier: string;
  projectId: number;
  runKey: string;
  sessionId: string | null;
  status: string;
  name: string | null;
  dataSourceName: string | null;
  projectTitle: string | null;
  projectBrand: string | null;
  segmentCount: number | null;
  totalPatientPopulation: number | null;
  runTimestamp: string | null;
  createdBy: number;
  createdOn: string;
  updatedOn: string;
}

export interface SegmentationRunRecord extends SegmentationRunSummary {
  marketView: Record<string, unknown> | null;
  qualInsights: Record<string, unknown> | null;
  profilesRoot: Record<string, unknown> | null;
  qualPostSeg: Record<string, unknown> | null;
  segmentationInputs: SegmentationInputs | null;
  files: Array<{
    id: number;
    runId: number;
    kind: string;
    path: string;
    filename: string;
    extension: string | null;
    sizeBytes: number | null;
    workspaceFileId: number | null;
    createdOn: string;
  }>;
  segments?: Array<Record<string, unknown>>;
}

export function createSegmentationRun(
  body: CreateSegmentationRunBody
): Promise<{ run: SegmentationRunRecord }> {
  return client()
    .post('/segmentation-runs', body)
    .then((r) => r.data);
}

export function getSegmentationRun(id: number): Promise<{ run: SegmentationRunRecord }> {
  return client()
    .get(`/segmentation-runs/${id}`)
    .then((r) => r.data);
}

export function listSegmentationRuns(params?: {
  projectId?: number;
  status?: 'incomplete' | 'complete';
  take?: number;
  skip?: number;
}): Promise<{ runs: SegmentationRunSummary[]; total: number }> {
  return client()
    .get('/segmentation-runs', { params })
    .then((r) => r.data);
}

export function updateSegmentationRun(
  id: number,
  patch: { status?: 'incomplete' | 'complete'; name?: string | null; segmentCount?: number | null }
): Promise<{ run: SegmentationRunRecord }> {
  return client()
    .patch(`/segmentation-runs/${id}`, patch)
    .then((r) => r.data);
}

// ─── Project Files (fallback for SegmentProfile Files tab) ──────────────────
// Hits chat-api's GET /projects/:projectId/files, which proxies through to
// the workspace table-items lookup. Used when a segment has no parent run
// (or the run has no files snapshot) and we want to show the project's full
// file library as a fallback for the Files tab.

export interface ProjectFileEntry {
  id: number;
  name: string;
  extension?: string | null;
  size?: number | null;
}

export interface ProjectFileItem {
  object: { id: number; name: string };
  file: ProjectFileEntry;
  [key: string]: unknown;
}

export interface ProjectFilesResponse {
  items: ProjectFileItem[];
  meta?: { count?: number; size?: number };
  breadcrumbs?: unknown[];
}

export function getProjectFiles(projectId: number): Promise<ProjectFilesResponse> {
  return client()
    .get(`/projects/${projectId}/files`)
    .then((r) => r.data);
}

// ─── Segments DB record (not page-only) ─────────────────────────────────────

export function createSegmentRecord(data: {
  projectId: number;
  name: string;
  description?: string;
  metadata?: Record<string, unknown>;
  /** Optional stripped metadata to write onto segments_v2 (without run-level
   *  blobs). Falls back to `metadata` server-side when omitted. */
  metadataV2?: Record<string, unknown>;
  iconUrl?: string;
  ttsSettings?: Record<string, unknown> | null;
  createdOn?: string;
  dashboardConfig?: Record<string, unknown>;
  identifier?: string;
  /** Parent segmentation run id — when present the server dual-writes a
   *  mirrored segments_v2 row and enqueues async persona generation. */
  segmentationRunId?: number;
}): Promise<{ segment: Record<string, unknown>; pageId: number; segmentV2Id: number | null }> {
  return client()
    .post('/segments', data)
    .then((r) => r.data);
}

export function getSegmentPersonaStatus(segmentV2Id: number): Promise<{
  personaStatus: 'pending' | 'generating' | 'complete' | 'failed';
  persona: string | null;
  matchedPersonaLabel: string | null;
}> {
  return client()
    .get(`/segments/v2/${segmentV2Id}/persona-status`)
    .then((r) => r.data);
}

export function enqueueSegmentPersona(segmentV2Id: number): Promise<{
  segmentV2Id: number;
  personaStatus: string;
}> {
  return client()
    .post(`/segments/v2/${segmentV2Id}/enqueue-persona`)
    .then((r) => r.data);
}

export function listSegments(params?: {
  hackathon?: boolean;
  // view=list returns a skinny shape ~10–50x smaller than the default.
  // Drops the heavy metadata blobs (marketView, qualInsights, quotes, full
  // segmentProfile) and only keeps what Explore Segments renders. See
  // serializeSegmentListView in chat-api/routers/v1/segments.ts for the
  // exact field set. Rows returned with view=list are tagged
  // metadata._listView = true so downstream consumers can detect that
  // they need to re-hydrate the fat row via getSegmentByPage.
  view?: 'list';
  // runId filters server-side to only return segments from a specific run.
  // Much faster than fetching all rows when only one run is needed.
  runId?: string;
}): Promise<{ segments: Array<Record<string, unknown>> }> {
  return client()
    .get('/segments', { params })
    .then((r) => r.data);
}

// Fetch a single full segment row by its dashboardPageId. Used by
// SegmentProfile when the nav-state DB record is a skinny list-view row
// (missing marketView / qualInsights / quotes) and we need to hydrate the
// fat shape for the market view panel and quotes card.
export function getSegmentByPage(pageId: number): Promise<{ segment: Record<string, unknown> }> {
  return client()
    .get(`/segments/by-page/${pageId}`)
    .then((r) => r.data);
}

/**
 * Deep-merge a partial metadata patch into the segment's metadata JSON.
 * Supports nested merge for `segmentProfile` and `quotes` keys.
 * Returns the reassembled fat segment row.
 */
export function patchSegmentMetadata(
  segmentV2Id: number,
  patch: Record<string, unknown>
): Promise<{ segment: Record<string, unknown> }> {
  return client()
    .patch(`/segments/${segmentV2Id}/metadata`, patch)
    .then((r) => r.data);
}

// ─── Segment Quote Extraction ──────────────────────────────────────────────

export interface SegmentQuoteItem {
  starredQuoteId: number | null;
  transcriptId: number;
  startTs: number;
  endTs: number;
  citationKey: string | null;
  value: string;
}

export interface ExtractSegmentQuotesResponse {
  subject: string;
  rationale: string;
  quotes: SegmentQuoteItem[];
  transcriptIdsUsed: number[];
  searchQueries: string[];
  quotesPerQuery: Record<string, number>;
}

export interface SegmentQuoteExemplarTheme {
  themeLabel: string;
  domain?: string;
  topQuote?: string;
}

export interface SegmentQuoteSentimentEntry {
  dimension: string;
  value: number;
}

export function extractSegmentQuotes(data: {
  parentObjectId: number;
  segmentName?: string;
  narrative: string;
  reasoningSummary?: string;
  qualImpact?: string;
  qualEnrichment?: string;
  matchedPersonaLabel?: string;
  personaDescription?: string;
  personaArchetype?: string;
  personaSentiment?: SegmentQuoteSentimentEntry[];
  exemplarThemes?: SegmentQuoteExemplarTheme[];
  transcriptIds?: number[];
}): Promise<ExtractSegmentQuotesResponse> {
  return client()
    .post('/segments/extract-quotes', data)
    .then((r) => r.data);
}

// ─── Async Quote Extraction (Queue-based) ──────────────────────────────────

export function enqueueSegmentQuotes(
  segmentId: number,
  data: {
    parentObjectId: number;
    segmentName?: string;
    narrative: string;
    reasoningSummary?: string;
    qualImpact?: string;
    qualEnrichment?: string;
    matchedPersonaLabel?: string;
    personaDescription?: string;
    personaArchetype?: string;
    personaSentiment?: SegmentQuoteSentimentEntry[];
    exemplarThemes?: SegmentQuoteExemplarTheme[];
    transcriptIds?: number[];
  }
): Promise<{ segmentId: number; quoteStatus: string }> {
  return client()
    .post(`/segments/${segmentId}/enqueue-quotes`, data)
    .then((r) => r.data);
}

export function getSegmentQuoteStatus(segmentId: number): Promise<{
  quoteStatus: string;
  quotes: { subject: string; rationale?: string; items: SegmentQuoteItem[] } | null;
}> {
  return client()
    .get(`/segments/${segmentId}/quote-status`)
    .then((r) => r.data);
}

// DISABLED: Segmentation-playground sink for PowerPoint use. The endpoint
// on the playground side will be added by that team; until then this helper
// (and the chat-api proxy it targets at
// `/segmentation/session/:sid/segment/:segid/quotes`) are commented out so
// we don't spam 404s on every save. Re-enable together with the chat-api
// proxy route in apps/chat-api/src/routers/v1/segmentation/segmentation.ts
// when the playground endpoint lands.
//
// export async function postSegmentQuotesToPlayground(data: {
//   sessionId: string;
//   segmentId: number | string;
//   subject: string;
//   quotes: SegmentQuoteItem[];
// }): Promise<void> {
//   try {
//     await client().post(`${BASE}/session/${data.sessionId}/segment/${data.segmentId}/quotes`, {
//       subject: data.subject,
//       quotes: data.quotes,
//     });
//   } catch {
//     /* endpoint may not exist yet — intentionally silent */
//   }
// }

// --- Qual Files ---

export interface QualServerFile {
  path: string;
  filename: string;
  extension: string;
  size_bytes: number;
}

export interface QualFileEntry {
  path: string;
  filename: string;
}

export interface QualFilesResponse {
  files: QualServerFile[];
}

export interface QualTranscriptsResponse {
  transcripts: Record<string, unknown>[];
}

export function getQualFiles(path?: string): Promise<QualFilesResponse> {
  return client()
    .get(`${BASE}/qual-files`, { params: path ? { path } : {} })
    .then((r) => r.data);
}

export function getDiscussionGuide(): Promise<Record<string, unknown>> {
  return client()
    .get(`${BASE}/discussion-guide`)
    .then((r) => r.data);
}

export function qualProcessFiles(
  sessionId: string,
  files: QualFileEntry[],
  forceReprocess = false
): Promise<unknown> {
  return client()
    .post(`${BASE}/qual-process/${sessionId}`, { files, force_reprocess: forceReprocess })
    .then((r) => r.data);
}

export function getQualTranscripts(sessionId: string): Promise<QualTranscriptsResponse> {
  return client()
    .get(`${BASE}/qual/${sessionId}/transcripts`)
    .then((r) => r.data);
}

export function getQualPersonas(sessionId: string): Promise<QualPersonasData> {
  return client()
    .get(`${BASE}/qual/${sessionId}/personas`)
    .then((r) => r.data);
}

export function updateQualPersonas(sessionId: string, body: Record<string, unknown>): Promise<unknown> {
  return client()
    .put(`${BASE}/qual/${sessionId}/personas`, body)
    .then((r) => r.data);
}

export function regenerateQualPersonas(sessionId: string): Promise<unknown> {
  return client()
    .post(`${BASE}/qual/${sessionId}/personas/regenerate`)
    .then((r) => r.data);
}

export function reconsolidateQual(sessionId: string): Promise<unknown> {
  return client()
    .post(`${BASE}/qual/${sessionId}/reconsolidate`)
    .then((r) => r.data);
}

export function setQualMode(
  sessionId: string,
  mode: string
): Promise<{
  features_rebuilt: boolean;
  status: string;
}> {
  return client()
    .put(`${BASE}/qual/${sessionId}/mode`, { mode })
    .then((r) => r.data);
}

export function getQualAlignment(sessionId: string): Promise<Record<string, unknown> | null> {
  return client()
    .get(`${BASE}/session/${sessionId}/state`)
    .then((r) => ((r.data as Record<string, unknown>).qual_alignment as Record<string, unknown>) ?? null);
}

export function updateQualAlignment(sessionId: string, themeOverrides: unknown[]): Promise<unknown> {
  return client()
    .put(`${BASE}/qual/${sessionId}/alignment`, { theme_overrides: themeOverrides })
    .then((r) => r.data);
}

export function getQualContextV2(sessionId: string): Promise<Record<string, unknown>> {
  return client()
    .get(`${BASE}/qual/${sessionId}/context-v2`)
    .then((r) => r.data);
}

export function getQualTaxonomy(sessionId: string): Promise<Record<string, unknown>> {
  return client()
    .get(`${BASE}/qual/${sessionId}/taxonomy`)
    .then((r) => r.data);
}

export function getEDAQualOverlay(sessionId: string): Promise<Record<string, unknown>> {
  return client()
    .get(`${BASE}/eda/${sessionId}/qual-overlay`)
    .then((r) => r.data);
}

export function getQualPostSegmentation(sessionId: string): Promise<Record<string, unknown>> {
  return client()
    .get(`${BASE}/qual/${sessionId}/post-segmentation`)
    .then((r) => r.data);
}

export function setResearchSegmentMode(sessionId: string, mode: string): Promise<unknown> {
  return client()
    .put(`${BASE}/qual/${sessionId}/research-segment-mode`, { mode })
    .then((r) => r.data);
}

// --- Segment Aggregated Data ---

export function getSegmentAggregatedData(
  segmentId: number
): Promise<{ item: SegmentAggregatedCardsResponse }> {
  return client()
    .get(`/segments/${segmentId}/aggregated-data`)
    .then((r) => r.data);
}

export function getSegmentCardCitations(
  segmentId: number,
  cardKey: string
): Promise<SegmentAggregatedDataAPI.GetCardCitations.Response> {
  return client()
    .get(`/segments/${segmentId}/card-citations`, { params: { cardKey } })
    .then((r) => r.data);
}

// ─── Segment Comparison (V2) ───────────────────────────────────────────────

export function getSegmentComparisonData(params: {
  segmentNames: string[];
  segmentationRunId: number;
}): Promise<SegmentComparisonAPI.Get.Response> {
  return client()
    .get('/segment-comparison', {
      params: {
        segments: params.segmentNames.join(','),
        segmentationRunId: params.segmentationRunId,
      },
    })
    .then((r) => r.data);
}

export function generateSegmentComparison(params: {
  segmentNames: string[];
  segmentationRunId: number;
}): Promise<SegmentComparisonAPI.Generate.Response> {
  return client()
    .post('/segment-comparison/generate', params)
    .then((r) => r.data);
}

export function regenerateSegmentComparisonExecutiveSummary(params: {
  segmentNames: string[];
  segmentationRunId: number;
}): Promise<{ segmentNamesKey: string; executiveSummaryStatus: 'generating' }> {
  return client()
    .post('/segment-comparison/regenerate-executive-summary', params)
    .then((r) => r.data);
}

export function getSegmentComparisonCardCitations(params: {
  segmentNames: string[];
  segmentationRunId: number;
  cardKey: string;
}): Promise<SegmentComparisonAPI.GetCardCitations.Response> {
  return client()
    .get('/segment-comparison/card-citations', {
      params: {
        segments: params.segmentNames.join(','),
        segmentationRunId: params.segmentationRunId,
        cardKey: params.cardKey,
      },
    })
    .then((r) => r.data);
}

// --- Chart Audit ---

export interface ChartAuditStudyOverview {
  sample_size?: number;
  charts_submitted?: number;
  methodology?: string;
  look_back_period?: string;
  fielding_dates?: string;
  [key: string]: unknown;
}

export interface ChartAuditSegment {
  name: string;
  n?: number;
  pct?: number | string;
  definition?: string;
  key_behavior?: string;
  [key: string]: unknown;
}

export interface ChartAuditDurationEntry {
  molecule_or_regimen: string;
  patient_type?: string;
  lot?: string;
  median_months?: number | null;
  [key: string]: unknown;
}

export interface ChartAuditBarrier {
  barrier: string;
  severity_or_pct?: number | null;
  molecule_or_category?: string;
  [key: string]: unknown;
}

export interface ChartAuditFutureIntent {
  molecule: string;
  increase_pct?: number | null;
  maintain_pct?: number | null;
  decrease_pct?: number | null;
  [key: string]: unknown;
}

export interface ChartAuditContext {
  study_overview?: ChartAuditStudyOverview;
  hcp_segments?: ChartAuditSegment[];
  market_shares_by_lot?: {
    '1L'?: Record<string, number>;
    '2L'?: Record<string, number>;
    [key: string]: Record<string, number> | undefined;
  };
  duration_benchmarks?: ChartAuditDurationEntry[];
  barriers?: ChartAuditBarrier[];
  future_intent?: ChartAuditFutureIntent[];
  key_findings?: string[];
  [key: string]: unknown;
}

export interface ChartAuditStatusResponse {
  available: boolean;
  files: string[];
  processed: boolean;
  study_overview?: ChartAuditStudyOverview | null;
}

export interface ChartAuditMatchedSegment {
  name: string;
  n?: number;
  pct?: number | string;
  score: number;
  quality?: string;
  definition?: string;
  key_behavior?: string;
}

export interface ChartAuditSegmentOverlay {
  segment_id: number;
  matched_chart_audit_segment?: ChartAuditMatchedSegment | null;
  market_share_comparison?: {
    chart_audit_1L?: Record<string, number>;
    chart_audit_2L?: Record<string, number>;
    [key: string]: Record<string, number> | undefined;
  };
  barriers?: ChartAuditBarrier[];
  duration_benchmarks?: ChartAuditDurationEntry[];
  future_intent?: ChartAuditFutureIntent[];
}

export interface ChartAuditOverlaysResponse {
  overlays: ChartAuditSegmentOverlay[];
  segment_matches?: unknown;
  [key: string]: unknown;
}

export interface ChartAuditStrategyContext {
  strategy?: string;
  context?: string;
  message?: string;
  study_overview?: ChartAuditStudyOverview;
  elderberry_position?: {
    '1L_share'?: number | null;
    '2L_share'?: number | null;
    [key: string]: number | null | undefined;
  };
  primary_segments?: ChartAuditSegment[];
  elderberry_future_intent?: ChartAuditFutureIntent;
  [key: string]: unknown;
}

export function getChartAuditStatus(): Promise<ChartAuditStatusResponse> {
  return client()
    .get(`${BASE}/chart-audit/status`)
    .then((r) => r.data);
}

export function processChartAudit(sessionId: string): Promise<ChartAuditContext> {
  return client()
    .post(`${BASE}/chart-audit/${sessionId}/process`, undefined, { timeout: 300_000 })
    .then((r) => r.data);
}

export function getChartAuditContext(sessionId: string): Promise<ChartAuditContext> {
  return client()
    .get(`${BASE}/chart-audit/${sessionId}/context`)
    .then((r) => r.data);
}

export function getChartAuditSegmentOverlays(sessionId: string): Promise<ChartAuditOverlaysResponse> {
  return client()
    .get(`${BASE}/chart-audit/${sessionId}/segment-overlays`)
    .then((r) => r.data);
}

export function getChartAuditStrategyContext(
  sessionId: string,
  strategyKey: string
): Promise<ChartAuditStrategyContext> {
  return client()
    .get(`${BASE}/chart-audit/${sessionId}/strategy-context`, { params: { strategy_key: strategyKey } })
    .then((r) => r.data);
}

export function setPersonaBasis(
  sessionId: string,
  basis: string
): Promise<{
  regenerated: boolean;
  features_rebuilt: boolean;
  fp_features_swapped: boolean;
}> {
  return client()
    .put(`${BASE}/qual/${sessionId}/persona-basis`, { basis })
    .then((r) => r.data);
}

// ─── Segmentation Run ↔ Message Testing Links ──────────────────────────────

export interface LinkedMessageTestingRun {
  id: number;
  status: string;
  brand: string | null;
  objective: string | null;
  messageCount: number;
  createdOn: string;
  linkedOn: string;
}

export function getLinkedMessageTests(segmentationRunId: number): Promise<LinkedMessageTestingRun[]> {
  return client()
    .get(`/segmentation-runs/${segmentationRunId}/message-tests`)
    .then((r) => r.data);
}

export function linkMessageTest(
  segmentationRunId: number,
  messageTestingRunId: number
): Promise<{ id: number }> {
  return client()
    .post(`/segmentation-runs/${segmentationRunId}/message-tests`, { messageTestingRunId })
    .then((r) => r.data);
}

export function unlinkMessageTest(segmentationRunId: number, messageTestingRunId: number): Promise<void> {
  return client().delete(`/segmentation-runs/${segmentationRunId}/message-tests/${messageTestingRunId}`);
}
